import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { useHoveredItemActions } from 'components/WorkspaceEditor/HoveredItemStore'
import { useCallback } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { DataNode } from '../../types'

/**
 * Custom hook that provides functions
 *
 * disableProductTypeInactivation - Determines whether the product type deactivation should be disabled for a given node.
 * handleProductTypeWarningDialogClose - a callback function to close warning dialog for show/hide restricted product type dialog
 */
export const useActivationDeactivationController = () => {
    const {
        setHoveredNode,
        setIsProductTypeDeactivationDialogOpen,
    } = useHoveredItemActions()

    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const isContentTeamFromStudioPage = isReactiveContentTeamFromStudioPage()
        .value
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    /**
     * Determines whether the product type deactivation should be disabled for a given node.
     */
    const disableProductTypeInactivation = useCallback(
        (node: Partial<DataNode>) => {
            if (
                !selectedProjectElementNode?.isInProject ||
                (isContentTeamFromStudioPage && !isCurrentlyOnOMWorkspace)
            )
                return true

            //  If the selected element type is 'PRODUCTTYPE', disables inactivation for article or product type content.
            const isRestrictedProductTypeActivation =
                node?.type === 'Article' || node?.isProductType

            // For selected product family    disables inactivation for nodes that are either product types or product groups.
            const isRestrictedProductTypeContentOnFamilyView =
                node.isProductType || node.isProductGroup

            return selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
                ? isRestrictedProductTypeActivation
                : isRestrictedProductTypeContentOnFamilyView
        },
        [
            isContentTeamFromStudioPage,
            selectedProjectElementNode,
            isCurrentlyOnOMWorkspace,
        ]
    )
    const handleProductTypeWarningDialogClose = useCallback(() => {
        setIsProductTypeDeactivationDialogOpen(false)
        setHoveredNode({})
    }, [setIsProductTypeDeactivationDialogOpen])

    return {
        handleProductTypeWarningDialogClose,
        disableProductTypeInactivation,
    }
}
